import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  User,
  Stethoscope,
  MoreHorizontal,
  FileText,
  Eye,
  Edit,
  Trash2,
} from "lucide-react";
import { format } from "date-fns";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/apiClient";
import { useDoctors } from "@/hooks/useDoctors";
import { useAuth } from "@/hooks/useAuth";
import { getPatientName, getDoctorName } from "@/utils/dataLookups";

export default function NotesList({
  notes,
  isLoading,
  onSelectNote,
  onEditNote,
  onDeleteNote,
}) {
  const { user } = useAuth();

  // Fetch patient details
  const { data: patients = [] } = useQuery({
    queryKey: ["patients"],
    queryFn: () => apiClient.get("/api/patients"),
  });

  // Fetch doctors
  const { data: doctors = [] } = useDoctors(user?.practiceId);

  // Function to strip HTML tags
  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array(3)
          .fill(0)
          .map((_, i) => (
            <Card
              key={i}
              className="bg-white/60 backdrop-blur-sm border-slate-200/60 p-6"
            >
              <div className="flex justify-between items-start">
                <div className="space-y-2">
                  <Skeleton className="h-5 w-48" />
                  <Skeleton className="h-4 w-32" />
                </div>
                <Skeleton className="h-8 w-8 rounded-full" />
              </div>
              <div className="mt-4 space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            </Card>
          ))}
      </div>
    );
  }

  if (notes.length === 0) {
    return (
      <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-lg">
        <CardContent className="p-12 text-center">
          <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">
            No Clinical Notes Found
          </h3>
          <p className="text-slate-600">
            Create your first clinical note to get started
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {notes.map((note) => (
          <Card
            key={note.id}
            className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col"
          >
            <CardContent className="p-6 flex-grow">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-blue-100 rounded-xl flex items-center justify-center">
                    <Stethoscope className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 text-lg">
                      {getPatientName(note.patientId, patients)}
                    </h3>
                    <p className="text-sm text-slate-500">
                      {note.date
                        ? format(new Date(note.date), "MMMM d, yyyy")
                        : "No date"
                      }
                    </p>
                  </div>
                </div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="w-8 h-8">
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => onSelectNote(note)}
                      className="gap-2"
                    >
                      <Eye className="w-4 h-4" /> View Details
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => onEditNote(note)}
                      className="gap-2"
                    >
                      <Edit className="w-4 h-4" /> Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => onDeleteNote(note.id)}
                      className="gap-2 text-red-600"
                    >
                      <Trash2 className="w-4 h-4" /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className="space-y-2">
                {note.consultation_notes && (
                  <div>
                    <div className="font-medium text-slate-800 text-sm mb-1">Consultation Notes:</div>
                    <p className="text-slate-600 text-sm line-clamp-2">
                      {stripHtml(note.consultation_notes)}
                    </p>
                  </div>
                )}
                {note.diagnosis && (
                  <div className="mt-2">
                    <div className="font-medium text-slate-800 text-sm mb-1">Diagnosis:</div>
                    <p className="text-slate-600 text-sm line-clamp-2">
                      {stripHtml(note.diagnosis)}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
            <div className="border-t border-slate-200/60 p-4 mt-auto">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <User className="w-4 h-4" />
                <span>{getDoctorName(note.doctorId, doctors)}</span>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
